import '../img/app-store.png';
import '../img/play-store.png';

import Component from '../../component/component';
import Helpers from '../../helpers/helpers';

import './navigation.scss';

const animationSpeed: number = 300;
const isSmallScreen: () => boolean = () => window.matchMedia('(max-width: 1089px)').matches;

export default class Navigation extends Component {
    static initSelector: string = '.js-navigation';

    backButtons: NodeListOf<HTMLElement>;
    items: NavigationItem[];

    constructor(target: HTMLElement) {
        super(target);

        this.backButtons = this.element.querySelectorAll('.navigation-header__button--back, .navigation-header__button--close');

        this.items = [];
        this.element.querySelectorAll('.navigation__item.has-children').forEach((item: HTMLElement) => {
            this.items.push(NavigationItem.create<typeof NavigationItem>(item));
        });

        this.backButtons.forEach((button: HTMLElement): void => {
            button.addEventListener('click', this.closeItems.bind(this));
        });
    }

    public closeItems(): void {
        for (const item of this.items) {
            item.close(false);
        }
    }
}

class NavigationItem extends Component {
    static initSelector: string = '.js-navigation-item';

    public isOpen: boolean = false;
    public list: HTMLElement;
    public navigation: HTMLElement[];
    public openEvent: Event;
    public closeEvent: Event;
    public animation: Animation;
    public screenWidth: number;

    constructor(target: HTMLElement) {
        super(target);

        this.list = this.element.querySelector('.navigation__list');
        this.navigation = Helpers.parents(this.element, '.navigation');
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.openEvent = new CustomEvent('openNavigationItem');
        this.closeEvent = new CustomEvent('closeNavigationItem');
        this.screenWidth = window.innerWidth;

        this.element.addEventListener('keyup', this.handleKeyup.bind(this));

        this.element.querySelectorAll('.navigation__link--with-children').forEach((link: HTMLElement) => {
            link.addEventListener('click', this.toggle.bind(this));
        });

        window.addEventListener('resize', this.handleResize);

        this.checkPositioning();
    }

    open(): void {
        this.isOpen = true;
        // on small screens, open with slide animation
        if (isSmallScreen()) {
            this.openWithAnimation();
        } else {
            this.navigation.forEach((item: HTMLElement) => item.classList.add('is-initialized'));
            this.checkPositioning();
            this.finishOpen();
        }

        window.dispatchEvent(this.openEvent);

        document.addEventListener('click', this.handleOutsideClick);
    }

    finishOpen(): void {
        this.element.classList.add('is-open');
        this.element.querySelectorAll('.navigation__link').forEach((link: HTMLElement) => link.setAttribute('aria-expanded', 'true'));
    }

    checkPositioning(): void {
        const child: HTMLElement = this.element.querySelector('.navigation__list--with-grandchildren');

        if (child && window) {
            const parentLeft: number = this.element.getBoundingClientRect().left;
            const style: CSSStyleDeclaration = window.getComputedStyle(child);
            const childWidth: number = child.getBoundingClientRect().width + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
            const containerWidth: number = document.body.clientWidth;

            if (parentLeft + childWidth >= containerWidth) {
                child.style.right = 'auto';
                child.style.left = containerWidth - (childWidth + parentLeft) + 'px';
                child.style.marginRight = '32px';
            }
        }
    }

    openWithAnimation(): void {
        const dropdownHeight: number = this.list.scrollHeight;

        Helpers.parents(this.list, '.navigation__list').forEach((item: HTMLElement) => item.style.transform = 'translateX(-100%)');
        Helpers.parents(this.list, '.navigation').forEach((item: HTMLElement) => item.style.height = this.list.scrollHeight + 'px');

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.list.animate(
            [
                {
                    clientHeight: 0,
                },
                {
                    clientHeight: dropdownHeight,
                },
            ],
            {
                duration: 0,
            },
        );

        this.animation.onfinish = (): void => {
            this.animation = null;
            this.list.removeAttribute('style');
            this.list.style.height = dropdownHeight + 'px';
            this.finishOpen();
        };
    }

    close(shouldFocusAfterClose: boolean): void {
        this.isOpen = false;

        // on small screens, close with slide animation
        if (isSmallScreen()) {
            Helpers.parents(this.list, '.navigation__list').forEach((item: HTMLElement) => item.removeAttribute('style'));
            Helpers.parents(this.list, '.navigation').forEach((item: HTMLElement) => item.removeAttribute('style'));
            if (Helpers.parents(this.list, '.header').length) {
                this.closeWithAnimation(shouldFocusAfterClose);
            } else {
                if (this.list){
                    this.list.removeAttribute('style');
                }
                this.finishClose(shouldFocusAfterClose);
            }
        } else {
            this.finishClose(shouldFocusAfterClose);
        }

        window.dispatchEvent(this.closeEvent);

        document.removeEventListener('click', this.handleOutsideClick);
    }

    finishClose(shouldFocus: boolean): void {
        this.element.classList.remove('is-open');
        this.element.querySelectorAll('.navigation__link').forEach((item: HTMLElement) => item.setAttribute('aria-expanded', 'false'));
        if (shouldFocus) {
            const firstLink: HTMLElement = this.element.querySelector('.navigation__link');

            if (firstLink) {
                firstLink.focus();
            }
        }
    }

    closeWithAnimation(shouldFocusAfterClose: boolean): void {
        if (this.animation) {
            this.animation.cancel();
        }

        setTimeout(() => {
            this.list.removeAttribute('style');
            this.finishClose(shouldFocusAfterClose);
        }, animationSpeed);
    }

    toggle(event: MouseEvent): void {
        event.preventDefault();

        if (this.element.classList.contains('is-open')) {
            this.close(false);
        } else {
            this.closeAll();
            this.open();
        }
    }

    closeAll(): void {
        const parentList: HTMLElement = Helpers.parents(this.element, '.navigation__list')[0];

        if (parentList?.hasChildNodes()) {
            parentList.childNodes.forEach((item: HTMLElement) => item.hasChildNodes() ? item.classList.remove('is-open') : '');
        }
    }

    handleOutsideClick(event: MouseEvent): void {
        if (!isSmallScreen()) {
            if (event.target instanceof Element && !event.target.closest('.' + this.element.className.replace(/ /g, '.'))) {
                this.close(false);
            }
        }
    }

    handleKeyup(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.close(true);
        }
    }

    handleResize(): void {
        const newScreenWidth: number = window.innerWidth;

        if (isSmallScreen()) {
            if (newScreenWidth !== this.screenWidth) {
                this.close(false);
                this.screenWidth = newScreenWidth;
            }
        } else {
            this.checkPositioning();
        }
    }

    destroy(): void {
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('click', this.handleOutsideClick);
    }
}
